import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SchemaOrg from '../../components/feature/SchemaOrg';
import { useSEO } from '../../hooks/useSEO';

const ServicesPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: '新北搬家服務項目【建鈞精緻搬家】台北桃園新竹搬家｜廢棄物清運｜垃圾處理｜人力搬工',
    description: '建鈞精緻搬家提供新北市、台北市、桃園市、新竹市專業搬家服務：套房搬遷、家庭搬家、辦公室搬遷、精緻搬家、廢棄物清運、垃圾處理、人力搬工。服務範圍涵蓋板橋、新莊、中和、永和、土城、三重、蘆洲、泰山、五股等地區。24小時服務，立即致電0978-900-081。',
    keywords: '新北搬家服務,台北搬家服務,桃園搬家,新竹搬家,套房搬遷,家庭搬家,辦公室搬遷,精緻搬家,廢棄物清運,垃圾處理,人力搬工,搬家公司推薦,板橋搬家,新莊搬家,中和搬家,永和搬家,土城搬家,三重搬家,蘆洲搬家,泰山搬家,五股搬家,搬家費用,搬家價格,專業搬家,24小時搬家,裝潢廢料清運,大型家具處理,搬家包裝,家具拆裝,鋼琴搬運',
    ogType: 'website',
    schema: {
      '@type': 'Service',
      name: '建鈞精緻搬家服務',
      description: '提供新北、台北、桃園、新竹地區全方位搬家服務，包含套房搬遷、家庭搬家、辦公室搬遷、精緻搬家、廢棄物清運、垃圾處理、人力搬工等專業服務',
      provider: {
        '@type': 'LocalBusiness',
        name: '建鈞精緻搬家',
        telephone: '+886-978-900-081',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '新北市',
          addressRegion: '新北市',
          addressCountry: 'TW'
        }
      },
      areaServed: [
        {
          '@type': 'City',
          name: '新北市'
        },
        {
          '@type': 'City',
          name: '台北市'
        },
        {
          '@type': 'City',
          name: '桃園市'
        },
        {
          '@type': 'City',
          name: '新竹市'
        }
      ],
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: '搬家服務項目',
        itemListElement: [
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '套房搬遷',
              description: '專業套房搬家服務，適合單身或小家庭，快速高效完成搬遷'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '家庭搬遷',
              description: '完整家庭搬遷規劃，從打包到定位，全程專業服務'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '精緻搬家',
              description: '專人到府打包裝箱，新家分房間定位，高品質搬家體驗'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '辦公室搬遷',
              description: '專業辦公室搬遷服務，確保設備安全，減少營運中斷時間'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '廢棄物清運',
              description: '提供廢棄物清運與處理服務，環保合法處理各類廢棄物'
            }
          },
          {
            '@type': 'Offer',
            itemOffered: {
              '@type': 'Service',
              name: '人力搬工',
              description: '提供專業搬運人力支援，協助重物搬運與垃圾處理'
            }
          }
        ]
      }
    }
  });

  const services = [
    {
      id: 1,
      icon: 'ri-home-4-line',
      title: '套房搬遷',
      description: '專業套房搬家服務，包含物品包裝、搬運、物品定位',
      features: [
        '專業包裝材料提供',
        '家具家電搬運',
        '物品定位服務',
        '樓層搬運服務'
      ],
      details: [
        '10-20坪套房空間',
        '單身租屋族搬家',
        '學生宿舍搬遷',
        '小家庭搬家需求'
      ],
      image: 'https://public.readdy.ai/ai/img_res/0ad0875aeb3dc70c1d6f2770ed06c0d2.jpg',
      price: '依現場估價',
      serviceArea: '新北市、台北市、桃園市、新竹市',
      bgColor: 'from-teal-500 to-emerald-500'
    },
    {
      id: 2,
      icon: 'ri-home-heart-line',
      title: '家庭搬遷',
      description: '完整家庭搬遷規劃，從打包到定位，全程專業服務',
      features: [
        '完整搬家規劃',
        '家具家電拆裝',
        '物品包裝保護',
        '新家物品定位'
      ],
      details: [
        '20-50坪家庭空間',
        '多房間家庭搬遷',
        '大型家具搬運',
        '完整家庭搬家需求'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20family%20moving%20service%20in%20Taiwan%2C%20movers%20carefully%20handling%20furniture%20and%20household%20items%20in%20a%20spacious%20family%20home%2C%20organized%20packing%20with%20family%20watching%2C%20bright%20warm%20interior%20with%20living%20room%20and%20dining%20area&width=800&height=600&seq=service-family&orientation=landscape',
      price: '依現場估價',
      serviceArea: '新北市、台北市、桃園市、新竹市'
    },
    {
      id: 3,
      icon: 'ri-vip-crown-line',
      title: '精緻搬家',
      description: '家中瑣事太多沒空整理？精緻搬家從幫您居家打包裝箱，再到新家紙箱分房間定位',
      features: [
        '專人到府打包裝箱',
        '物品分類整理',
        '安全運送搬遷',
        '新家分房間定位'
      ],
      details: [
        '忙碌上班族沒時間整理',
        '家中物品繁多需要協助',
        '希望全程有專人服務',
        '追求高品質搬家體驗'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20moving%20team%20providing%20premium%20packing%20service%20in%20Taiwanese%20home%2C%20workers%20carefully%20organizing%20and%20packing%20household%20items%20with%20quality%20materials%2C%20bright%20organized%20interior%20with%20systematic%20labeling&width=800&height=600&seq=service-premium&orientation=landscape',
      price: '依現場估價',
      serviceArea: '新北市、台北市、桃園市、新竹市'
    },
    {
      id: 4,
      icon: 'ri-building-line',
      title: '辦公室搬遷',
      description: '企業辦公室搬遷服務，辦公室OA拆裝服務，物品定位',
      features: [
        '辦公設備拆裝',
        'OA辦公家具處理',
        '文件資料搬運',
        '辦公室物品定位'
      ],
      details: [
        '公司行號搬遷',
        '辦公室擴編或縮編',
        '可配合假日或夜間搬遷',
        '快速高效不影響營運'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20corporate%20office%20moving%20service%20in%20Taiwan%2C%20experienced%20movers%20dismantling%20and%20packing%20office%20cubicles%20and%20computer%20workstations%2C%20modern%20commercial%20office%20environment%20with%20organized%20boxes%20and%20bubble%20wrap%20protection%2C%20professional%20moving%20crew%20wearing%20safety%20uniforms%20handling%20electronic%20equipment%20with%20care%2C%20bright%20office%20space%20with%20fluorescent%20lighting%20and%20carpet%20flooring%2C%20systematic%20office%20relocation%20process&width=800&height=600&seq=service-office-relocation&orientation=landscape',
      price: '依現場估價',
      serviceArea: '新北市、台北市、桃園市、新竹市'
    },
    {
      id: 5,
      icon: 'ri-delete-bin-line',
      title: '家庭廢棄物處理',
      description: '協助清理家庭廢棄物,提供完整的垃圾處理與環保回收服務',
      features: [
        '廢棄物分類',
        '環保回收處理',
        '大型家具清運',
        '裝潢廢料清理'
      ],
      details: [
        '搬家前後垃圾清運',
        '老舊家具汰換',
        '裝潢後廢料處理',
        '大掃除垃圾清運'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20waste%20disposal%20service%20in%20Taiwan%2C%20workers%20sorting%20and%20removing%20household%20waste%20and%20old%20furniture%2C%20organized%20recycling%20process%2C%20clean%20efficient%20operation%20with%20safety%20equipment&width=800&height=600&seq=service-waste&orientation=landscape',
      price: '依現場估價',
      serviceArea: '新北市、台北市、桃園市、新竹市'
    },
    {
      id: 6,
      icon: 'ri-team-line',
      title: '人力搬工',
      description: '提供專業搬運人力支援,協助重物搬運與垃圾處理',
      features: [
        '專業搬運人員',
        '重物搬運服務',
        '樓層搬運協助',
        '彈性人力調配'
      ],
      details: [
        '需要額外人力協助',
        '重型物品搬運',
        '高樓層搬運需求',
        '短期人力支援'
      ],
      image: 'https://readdy.ai/api/search-image?query=Professional%20moving%20labor%20team%20in%20Taiwan%2C%20strong%20workers%20in%20uniform%20safely%20carrying%20heavy%20furniture%20and%20boxes%2C%20teamwork%20coordination%20with%20proper%20lifting%20techniques%2C%20bright%20daylight%20outdoor%20scene&width=800&height=600&seq=service-labor&orientation=landscape',
      price: '依現場估價',
      serviceArea: '新北市、台北市、桃園市、新竹市'
    }
  ];

  const advantages = [
    {
      icon: 'ri-shield-check-line',
      title: '全程保險',
      description: '物品損壞有保障',
      detail: '投保營業責任險，讓您安心無憂'
    },
    {
      icon: 'ri-truck-line',
      title: '專業車輛',
      description: '各式車型任選',
      detail: '5噸貨車配備齊全，適合各種搬家需求'
    },
    {
      icon: 'ri-box-3-line',
      title: '包材提供',
      description: '紙箱氣泡紙齊全',
      detail: '提供專業包裝材料，保護您的物品'
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '貼心服務',
      description: '專人全程協助',
      detail: '從諮詢到完成，全程專人服務'
    }
  ];

  // 服務地區資料
  const serviceAreas = [
    {
      city: '新北市',
      districts: ['板橋區', '新莊區', '中和區', '永和區', '土城區', '三重區', '蘆洲區', '泰山區', '五股區', '林口區', '新店區', '淡水區', '汐止區', '樹林區', '鶯歌區', '三峽區', '八里區', '萬里區', '金山區', '瑞芳區'],
      icon: 'ri-community-line',
      color: 'emerald'
    },
    {
      city: '台北市',
      districts: ['中正區', '大同區', '中山區', '松山區', '大安區', '萬華區', '信義區', '士林區', '北投區', '內湖區', '南港區', '文山區'],
      icon: 'ri-building-line',
      color: 'blue'
    },
    {
      city: '桃園市',
      districts: ['桃園區', '中壢區', '平鎮區', '八德區', '楊梅區', '蘆竹區', '龜山區', '龍潭區', '大溪區', '大園區', '新屋區', '觀音區', '復興區'],
      icon: 'ri-plane-line',
      color: 'orange'
    },
    {
      city: '新竹市縣',
      districts: ['東區', '北區', '香山區', '竹北市', '竹東鎮', '新埔鎮', '關西鎮', '湖口鄉', '新豐鄉', '芎林鄉', '橫山鄉', '北埔鄉', '寶山鄉', '峨眉鄉'],
      icon: 'ri-computer-line',
      color: 'purple'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Service Schema - 主要服務 */}
      <SchemaOrg
        type="Service"
        data={{
          name: '專業搬家服務',
          description: '提供全方位搬家服務，包含家庭搬家、辦公室搬遷、鋼琴搬運等專業服務',
          provider: {
            name: '專業搬家公司',
            telephone: '+886-2-1234-5678'
          },
          areaServed: '台灣北部地區',
          price: '3000'
        }}
      />

      {/* Product Schema - 各項服務產品 */}
      {services.map((service, index) => (
        <SchemaOrg
          key={index}
          type="Product"
          data={{
            name: service.title,
            description: service.description,
            brand: '專業搬家公司',
            price: service.price,
            priceValidUntil: '2026-12-31',
            aggregateRating: {
              ratingValue: '4.8',
              reviewCount: '156'
            }
          }}
        />
      ))}

      {/* 右上角導航選單 */}
      <div className="absolute top-8 right-8 flex gap-6 text-sm font-medium z-10">
        <Link 
          to="/"
          className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-home-4-line text-base group-hover:scale-110 transition-transform"></i>
          <span>首頁</span>
        </Link>
        <Link 
          to="/about"
          className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-information-line text-base group-hover:scale-110 transition-transform"></i>
          <span>關於我們</span>
        </Link>
        <Link 
          to="/services"
          className="text-teal-600 font-semibold cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-service-line text-base group-hover:scale-110 transition-transform"></i>
          <span>服務項目</span>
        </Link>
        <Link 
          to="/service-area"
          className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-map-pin-line text-base group-hover:scale-110 transition-transform"></i>
          <span>服務範圍</span>
        </Link>
        <Link 
          to="/pricing"
          className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-calculator-line text-base group-hover:scale-110 transition-transform"></i>
          <span>搬家費用</span>
        </Link>
        <Link 
          to="/cases"
          className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-gallery-line text-base group-hover:scale-110 transition-transform"></i>
          <span>成功案例</span>
        </Link>
        <Link 
          to="/knowledge"
          className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-book-open-line text-base group-hover:scale-110 transition-transform"></i>
          <span>搬家知識</span>
        </Link>
        <Link 
          to="/faq"
          className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-question-line text-base group-hover:scale-110 transition-transform"></i>
          <span>常見問題</span>
        </Link>
        <Link 
          to="/contact"
          className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 group"
        >
          <i className="ri-customer-service-line text-base group-hover:scale-110 transition-transform"></i>
          <span>聯絡我們</span>
        </Link>
      </div>

      {/* Hero Section - 優化設計 */}
      <section className="relative py-20 bg-gradient-to-br from-teal-50 via-white to-emerald-50 overflow-hidden">
        {/* 動態背景元素 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-teal-200/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-200/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full text-teal-700 text-sm font-semibold mb-6">
              <i className="ri-service-line"></i>
              <span>專業搬家服務</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">建鈞精緻搬家 專業服務項目</h1>
            <p className="text-2xl text-gray-600 mb-4 leading-relaxed">
              新北｜台北｜桃園｜新竹 全區域專業搬家服務
            </p>
            <p className="text-xl text-gray-500 mb-6">
              套房搬遷 · 家庭搬家 · 辦公室搬遷 · 精緻搬家 · <strong className="text-teal-600">廢棄物清運</strong> · <strong className="text-teal-600">垃圾處理</strong> · <strong className="text-teal-600">人力搬工</strong>
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm text-gray-600 mb-8">
              <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-medium hover:bg-teal-200 transition-colors">板橋搬家</span>
              <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-medium hover:bg-teal-200 transition-colors">新莊搬家</span>
              <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-medium hover:bg-teal-200 transition-colors">中和搬家</span>
              <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-medium hover:bg-teal-200 transition-colors">永和搬家</span>
              <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-medium hover:bg-teal-200 transition-colors">土城搬家</span>
              <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-medium hover:bg-teal-200 transition-colors">三重搬家</span>
              <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-medium hover:bg-teal-200 transition-colors">蘆洲搬家</span>
              <span className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-medium hover:bg-teal-200 transition-colors">泰山搬家</span>
            </div>
          </div>
          
          {/* 快速聯絡區 - 優化設計 */}
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl mx-auto border-2 border-teal-100">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
                <i className="ri-customer-service-line text-teal-600 text-2xl"></i>
                立即諮詢，免費估價
              </h3>
              <p className="text-gray-600">專人為您服務，30 分鐘內回覆</p>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="tel:0978900081"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer shadow-lg whitespace-nowrap"
              >
                <i className="ri-phone-fill text-2xl"></i>
                <span>0978-900-081</span>
              </a>
              <a 
                href="https://line.me/ti/p/@285yavdh" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer shadow-lg whitespace-nowrap"
              >
                <img 
                  src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                  alt="LINE" 
                  className="w-6 h-6"
                />
                <span>官方LINE</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid - 優化卡片設計 */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">我們提供的專業服務</h2>
            <p className="text-xl text-gray-600">專業、安全、快速，滿足您的各種搬家需求</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-3xl shadow-xl overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-2 group">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-4 left-4 bg-gradient-to-r from-teal-600 to-emerald-600 text-white p-3 rounded-full shadow-lg">
                    <i className={`${service.icon} text-3xl`}></i>
                  </div>
                  <div className="absolute bottom-4 right-4 bg-white/95 backdrop-blur-sm px-5 py-2 rounded-full shadow-lg">
                    <span className="text-sm font-bold text-teal-600">{service.price}</span>
                  </div>
                </div>
                
                <div className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-4 text-lg leading-relaxed">{service.description}</p>
                  
                  {/* 服務地區 */}
                  <div className="mb-6 bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-teal-700">
                      <i className="ri-map-pin-line text-xl"></i>
                      <span className="font-semibold">服務地區：</span>
                      <span className="text-sm">{service.serviceArea}</span>
                    </div>
                  </div>
                  
                  {/* 服務特色 */}
                  <div className="mb-6">
                    <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <i className="ri-star-line text-yellow-500"></i>
                      服務特色
                    </h4>
                    <div className="grid grid-cols-2 gap-3">
                      {service.features.map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-gray-700 bg-teal-50 px-3 py-2 rounded-lg hover:bg-teal-100 transition-colors">
                          <i className="ri-checkbox-circle-fill text-teal-600 mr-2 text-lg"></i>
                          <span className="font-medium text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* 適用情況 */}
                  <div className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors">
                    <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                      <i className="ri-information-line text-teal-600"></i>
                      適用情況
                    </h4>
                    <ul className="space-y-2">
                      {service.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start text-gray-600 text-sm">
                          <i className="ri-arrow-right-s-line text-teal-600 mt-0.5 mr-1"></i>
                          <span>{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 服務地區展示 */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">服務範圍涵蓋</h2>
            <p className="text-xl text-gray-600">新北、台北、桃園、新竹全區域專業服務</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {serviceAreas.map((area, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all hover:-translate-y-1">
                <div className={`w-16 h-16 bg-${area.color}-100 rounded-xl flex items-center justify-center mb-4`}>
                  <i className={`${area.icon} text-3xl text-${area.color}-600`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{area.city}</h3>
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-gray-700 mb-2">服務區域：</p>
                  <div className="flex flex-wrap gap-2">
                    {area.districts.slice(0, 6).map((district, idx) => (
                      <span key={idx} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        {district}
                      </span>
                    ))}
                    {area.districts.length > 6 && (
                      <span className="text-xs text-gray-500 px-2 py-1">
                        等 {area.districts.length} 個區域
                      </span>
                    )}
                  </div>
                </div>
                <Link 
                  to={`/area/${area.city === '新北市' ? 'new-taipei' : area.city === '台北市' ? 'taipei' : area.city === '桃園市' ? 'taoyuan' : 'hsinchu'}`}
                  className="mt-4 inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-sm transition-colors cursor-pointer"
                >
                  查看詳細
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">為什麼選擇建鈞精緻搬家？</h2>
            <p className="text-xl text-gray-600">專業保障，讓您搬家無後顧之憂</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {advantages.map((advantage, index) => (
              <div key={index} className="bg-gradient-to-br from-teal-50 to-emerald-50 p-8 rounded-2xl shadow-md text-center hover:shadow-xl transition-all hover:-translate-y-1">
                <div className="bg-teal-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <i className={`${advantage.icon} text-white text-4xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{advantage.title}</h3>
                <p className="text-teal-600 font-semibold mb-3">{advantage.description}</p>
                <p className="text-gray-600 text-sm leading-relaxed">{advantage.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gradient-to-br from-teal-600 via-emerald-600 to-teal-700 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">服務流程超簡單</h2>
            <p className="text-xl text-teal-100">三步驟完成搬家，輕鬆又安心</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all">
              <div className="bg-white text-teal-600 w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-xl">
                1
              </div>
              <h3 className="text-2xl font-bold mb-3">聯絡我們</h3>
              <p className="text-teal-100 text-lg leading-relaxed">
                電話或 LINE 告訴我們您的需求<br/>
                <span className="text-sm">30 分鐘內快速回覆</span>
              </p>
            </div>

            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all">
              <div className="bg-white text-teal-600 w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-xl">
                2
              </div>
              <h3 className="text-2xl font-bold mb-3">免費估價</h3>
              <p className="text-teal-100 text-lg leading-relaxed">
                專人到府評估，報價透明清楚<br/>
                <span className="text-sm">絕無隱藏費用</span>
              </p>
            </div>

            <div className="text-center bg-white/10 backdrop-blur-sm rounded-2xl p-8 hover:bg-white/20 transition-all">
              <div className="bg-white text-teal-600 w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mx-auto mb-6 shadow-xl">
                3
              </div>
              <h3 className="text-2xl font-bold mb-3">安心搬家</h3>
              <p className="text-teal-100 text-lg leading-relaxed">
                準時到達，專業搬運完成<br/>
                <span className="text-sm">全程保險保障</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">常見問題</h2>
            <p className="text-xl text-gray-600">快速了解我們的服務</p>
          </div>

          <div className="space-y-6">
            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="bg-teal-100 p-3 rounded-full flex-shrink-0">
                  <i className="ri-question-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">搬家需要提前多久預約？</h4>
                  <p className="text-gray-600 leading-relaxed">
                    建議提前 3-7 天預約，讓我們有充足時間安排人力與車輛。如果是急件搬家，我們也會盡力配合您的時間。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="bg-teal-100 p-3 rounded-full flex-shrink-0">
                  <i className="ri-question-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">搬家費用如何計算？</h4>
                  <p className="text-gray-600 leading-relaxed">
                    費用會根據搬運距離、樓層、物品數量、特殊狀況等因素計算。我們提供免費到府估價，給您最準確的報價，絕無隱藏費用。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
              <div className="flex items-start gap-4">
                <div className="bg-teal-100 p-3 rounded-full flex-shrink-0">
                  <i className="ri-question-line text-teal-600 text-xl"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-3">物品損壞怎麼辦？</h4>
                  <p className="text-gray-600 leading-relaxed">
                    我們有投保營業責任險，搬運過程中若有物品損壞，會依照保險條款進行理賠。我們的專業團隊會用心保護每一件物品。
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link 
              to="/faq"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold text-lg transition-colors cursor-pointer"
            >
              查看更多常見問題
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">需要搬家服務嗎？</h2>
          <p className="text-xl text-gray-600 mb-10 leading-relaxed">
            立即聯絡我們，讓專業團隊為您服務<br/>
            <span className="text-lg text-gray-500">免費估價 · 透明收費 · 安全保障</span>
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-8">
            <a 
              href="https://line.me/ti/p/@285yavdh" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-green-600 text-white px-10 py-4 rounded-full text-lg font-bold hover:bg-green-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-3 shadow-lg"
            >
              <img 
                src="https://static.readdy.ai/image/9ef47c451bf9aafd7a63eea67af7d95d/c33a3c3f2fa31df7dfad05bfabc09369.webp" 
                alt="LINE" 
                className="w-6 h-6"
              />
              官方LINE
            </a>

            <a 
              href="tel:0978900081"
              className="bg-white border-2 border-teal-600 text-teal-600 px-10 py-4 rounded-full text-lg font-bold hover:bg-teal-50 transition-colors cursor-pointer whitespace-nowrap shadow-lg"
            >
              立即致電
            </a>
          </div>

          <div className="pt-8 border-t border-gray-200">
            <p className="text-gray-600">
              <i className="ri-time-line mr-2"></i>
              營業時間：週一至週日 8:00-20:00 | 24 小時線上諮詢
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
